import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Table2, Lock, Search } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTh, sortRows, dateValue, type TableSort, type SortKind } from '@/components/ui/sortable-table';
import { fetchMappingTable, type MappingTableRow } from '@/hooks/useMappingTable';

function formatDate(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function houseLabel(r: MappingTableRow) {
  return [r.houseNumber, r.houseNumberExtension].filter(Boolean).join(' ') || '—';
}

// The 14 columns, in display order — one place declares the label key, the sort kind and the value
// read for sorting, so a column can never be sortable in the header and unsorted in the comparator.
const COLUMNS: { key: string; kind: SortKind; value: (r: MappingTableRow) => string | number | null }[] = [
  { key: 'jvid', kind: 'number', value: (r) => r.jvid },
  { key: 'operator', kind: 'text', value: (r) => r.operatorLabel },
  { key: 'source', kind: 'text', value: (r) => (r.isDemo ? 'demo' : 'dmp') },
  { key: 'street', kind: 'text', value: (r) => r.streetName },
  { key: 'houseNumber', kind: 'text', value: (r) => r.houseNumber },
  { key: 'busNumber', kind: 'text', value: (r) => r.busNumber },
  { key: 'apartment', kind: 'text', value: (r) => r.appartmentNumber },
  { key: 'postalCode', kind: 'text', value: (r) => r.postalCode },
  { key: 'municipality', kind: 'text', value: (r) => r.municipality },
  { key: 'city', kind: 'text', value: (r) => r.city },
  { key: 'dwellingUnitType', kind: 'text', value: (r) => r.dwellingUnitType },
  { key: 'scopelist', kind: 'text', value: (r) => r.scopelist },
  { key: 'modifiedBy', kind: 'text', value: (r) => r.modifiedBy },
  { key: 'updatedAt', kind: 'date', value: (r) => dateValue(r.updatedAt) },
];
const COLUMN_VALUE = new Map(COLUMNS.map((c) => [c.key, c.value]));

/** The date part of an ISO timestamp, for comparing against the <input type="date"> bounds. */
function dayOf(iso: string | null): string | null {
  return iso ? iso.slice(0, 10) : null;
}

// Admin-only READ-ONLY raw view of the DMP address_mapping_table. Lets an admin scan the operator
// submissions and understand what reviewers are correcting, without opening tickets one by one.
export function MappingTable() {
  const { t } = useTranslation('common');
  const [rows, setRows] = useState<MappingTableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  // Anouk 2026-08-04: « un filtre par champ, par exemple utilisateur, opérateur » + un classement
  // « des plus anciens aux plus récents ». Tout est client-side — la page charge déjà tout le jeu.
  const [byModifier, setByModifier] = useState('');
  const [byOperator, setByOperator] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [sort, setSort] = useState<TableSort | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMappingTable();
      setRows(res.data);
    } catch (err: any) {
      toast.error(err?.message || t('mappingTable.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Distinct values offered by the two dropdowns — read from the loaded rows, so an operator or a
  // colleague who never touched the table never shows up as an empty option.
  const modifiers = useMemo(
    () => [...new Set(rows.map((r) => r.modifiedBy).filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b, 'fr')),
    [rows],
  );
  const operators = useMemo(
    () => [...new Set(rows.map((r) => r.operatorLabel).filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b, 'fr')),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const kept = rows.filter((r) => {
      if (byModifier && r.modifiedBy !== byModifier) return false;
      if (byOperator && r.operatorLabel !== byOperator) return false;
      // Date bounds are inclusive on both ends and compare calendar days, not instants — picking the
      // same day in both fields must return that whole day.
      const day = dayOf(r.updatedAt);
      if (fromDay && (day == null || day < fromDay)) return false;
      if (toDay && (day == null || day > toDay)) return false;
      if (!q) return true;
      return [String(r.jvid), r.operatorLabel, r.streetName, r.city, r.municipality, r.postalCode, r.busNumber, r.modifiedBy, r.isDemo ? 'demo' : 'dmp']
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    return sortRows(kept, sort, (r, key) => COLUMN_VALUE.get(key)?.(r) ?? null);
  }, [rows, query, byModifier, byOperator, fromDay, toDay, sort]);

  const demoCount = useMemo(() => rows.filter((r) => r.isDemo).length, [rows]);
  const realCount = rows.length - demoCount;
  const filtersOn = byModifier !== '' || byOperator !== '' || fromDay !== '' || toDay !== '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <Table2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('mappingTable.title')}</h2>
          <p className="text-sm text-slate-500">{t('mappingTable.subtitle', { count: rows.length })}</p>
          {demoCount > 0 && (
            <p className="text-xs text-slate-400">{t('mappingTable.breakdown', { real: realCount, demo: demoCount })}</p>
          )}
        </div>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
          <Lock className="w-3 h-3" /> {t('mappingTable.readOnlyBadge')}
        </span>
      </div>

      {/* Search + field filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1 min-w-[16rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('mappingTable.searchPlaceholder')}
            className="w-full h-9 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
          />
        </div>
        <select
          value={byModifier}
          onChange={(e) => setByModifier(e.target.value)}
          aria-label={t('mappingTable.filterModifiedBy')}
          className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
        >
          <option value="">{t('mappingTable.filterModifiedBy')}</option>
          {modifiers.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select
          value={byOperator}
          onChange={(e) => setByOperator(e.target.value)}
          aria-label={t('mappingTable.filterOperator')}
          className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
        >
          <option value="">{t('mappingTable.filterOperator')}</option>
          {operators.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input
          type="date"
          value={fromDay}
          onChange={(e) => setFromDay(e.target.value)}
          aria-label={t('mappingTable.filterFrom')}
          title={t('mappingTable.filterFrom')}
          className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
        />
        <input
          type="date"
          value={toDay}
          onChange={(e) => setToDay(e.target.value)}
          aria-label={t('mappingTable.filterTo')}
          title={t('mappingTable.filterTo')}
          className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
        />
        {filtersOn && (
          <button
            type="button"
            onClick={() => { setByModifier(''); setByOperator(''); setFromDay(''); setToDay(''); }}
            className="h-9 rounded-lg px-2.5 text-sm font-semibold text-brand-rose hover:underline"
          >
            {t('mappingTable.filterClear')}
          </button>
        )}
        {(filtersOn || query.trim() !== '') && (
          <span className="text-xs text-slate-400">
            {t('mappingTable.filterCount', { shown: filtered.length, total: rows.length })}
          </span>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-400">
          {rows.length === 0 ? t('mappingTable.empty') : t('mappingTable.noMatch')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              {/* Every column sorts (Anouk 2026-08-04) — declared once in COLUMNS above. */}
              <tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                {COLUMNS.map((c) => (
                  <SortableTh
                    key={c.key}
                    label={t(`mappingTable.col.${c.key}`)}
                    sortKey={c.key}
                    kind={c.kind}
                    sort={sort}
                    onSort={setSort}
                    title={t('mappingTable.sortHint')}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={`${r.jvid}-${r.operatorId}`} className="border-t border-slate-100 hover:bg-brand-rose/[0.03]">
                  <td className="px-3 py-2.5 font-medium text-slate-800">{r.jvid}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.operatorLabel}</td>
                  <td className="px-3 py-2.5">
                    {r.isDemo ? (
                      <span title={t('mappingTable.sourceDemoHint')} className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                        {t('mappingTable.sourceDemo')}
                      </span>
                    ) : (
                      <span title={t('mappingTable.sourceRealHint')} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                        {t('mappingTable.sourceReal')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{r.streetName || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{houseLabel(r)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.busNumber || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.appartmentNumber || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.postalCode || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.municipality || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.city || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.dwellingUnitType || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.scopelist || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-500">{r.modifiedBy || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-500">{formatDate(r.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
